import { Form } from 'antd';
import { get } from 'lodash';
import React, { memo, useEffect } from 'react';
import { getCodes } from '@xbcb/ui-utils';
import DecimalQuantity from 'components/DecimalQuantity';
import PendingUpdatesHighlight from 'components/PendingUpdatesHighlight';
import { useHtsValues } from 'context/htsValues';
import { setProductUnitQuantity } from 'libs/commercialInvoiceProductHelpers';
import { useWorkOrderTaskConfiguration } from 'libs/hooks';
import { shouldRequire } from 'libs/readWriteControl';
import { defaultUOM, isUOMEditAllowedForHts } from 'libs/uomDefaults';
import {
  checkUOMOrder,
  getUomDisplayText,
  reorderQuantities,
} from 'libs/uomUtils';
import { useTheme } from 'styled-components';
import { CommercialInvoiceBaseProps } from 'types';
import { StyledTariffUnitQuantityWrapperDiv } from './styles';

export interface LineItemTariffUnitQuantitiesProps
  extends CommercialInvoiceBaseProps {
  index: number;
}

/**
 * LineItemTariffUnitQuantities
 *
 * The unit reporting quantity input fields for LineItem Tariff tabs.
 */

export const LineItemTariffUnitQuantities: React.FC<
  LineItemTariffUnitQuantitiesProps
> = ({ fullNamePath, localNamePath, disabled, readOnly, required, index }) => {
  const theme = useTheme();
  const { readWriteControlsConfig } = useWorkOrderTaskConfiguration();
  const form = Form.useFormInstance();

  const { htsValues } = useHtsValues();
  const htsNumber = htsValues.htsNumbers[index];
  const htsObj = htsValues.htsObjects[index];

  const codes = getCodes();
  const { CATAIR: CATAIRCodes = {} } = get(codes, 'CBP', {});
  const { unit: unitCodes = {}, lockedUnit: lockedUnitCodes = {} } =
    CATAIRCodes;

  const fullProductNamePath = [...fullNamePath.slice(0, 4)];
  const fullUnitQuantitiesNamePath = [
    ...fullNamePath,
    'unitReportingQuantities',
  ];
  const unitQuantitiesNamePath = [...localNamePath, 'unitReportingQuantities'];

  useEffect(() => {
    let unitReportingQuantities = form.getFieldValue(
      fullUnitQuantitiesNamePath,
    );

    /**
     * If we are loading a saved record, the order of the tariff.reportingQuantities
     * may be different from the order in which the UOMs appear in the HTS object, which
     * throws off the index-based form mapping below. In this case, syncronize the order.
     */
    if (!checkUOMOrder(unitReportingQuantities, htsObj?.uom)) {
      // There is a UOM/unit mismatch
      unitReportingQuantities = reorderQuantities(
        unitReportingQuantities,
        htsObj?.uom,
        true,
      );
      form.setFieldValue(fullUnitQuantitiesNamePath, unitReportingQuantities);
    }

    htsObj?.uom?.map((uom: string, uomIdx: number) => {
      const isDefaultUOM = defaultUOM.includes(uom);
      let unitReportingQuantity = unitReportingQuantities?.[uomIdx]?.value;
      // Only set default values if no value exists at all (preserve calculated values)
      if (
        isDefaultUOM &&
        (unitReportingQuantity === undefined || unitReportingQuantity === null)
      ) {
        unitReportingQuantity = lockedUnitCodes[uom];
        setProductUnitQuantity({
          form,
          productNamePath: fullProductNamePath,
          uomIdx,
          unitReportingQuantity: lockedUnitCodes[uom],
          realTariffIndex: index,
        });
      }
    });
  }, [form, fullNamePath, htsObj, index, lockedUnitCodes]);

  const uoms =
    htsNumber && htsObj?.uom?.length && htsObj.uom[0] !== 'X'
      ? htsObj.uom
      : null;

  return (
    <StyledTariffUnitQuantityWrapperDiv>
      {!!htsNumber &&
        uoms?.map((uom: string, i: number) => {
          const unitText = `Unit ${getUomDisplayText(unitCodes, uom, i)}`;
          const defaultUOMValue = defaultUOM.includes(uom)
            ? lockedUnitCodes[uom]
            : undefined;
          return (
            <PendingUpdatesHighlight
              form={form}
              fullNamePath={[...fullUnitQuantitiesNamePath, i, 'value']}
            >
              <DecimalQuantity
                key={uom}
                debounce={true}
                dataCySuffix={unitText}
                field={[...unitQuantitiesNamePath, i, 'value']}
                label={unitText}
                defaultValue={defaultUOMValue}
                required={shouldRequire(
                  'invoices.products.lines.tariffs.unitReportingQuantities.value',
                  readWriteControlsConfig,
                  required,
                )}
                $itemSize={theme.size.tiny_short}
                units={uom.toLowerCase()}
                readOnly={
                  readOnly ||
                  (Boolean(defaultUOMValue) &&
                    !isUOMEditAllowedForHts(htsNumber))
                }
                disabled={
                  disabled ||
                  (Boolean(defaultUOMValue) &&
                    !isUOMEditAllowedForHts(htsNumber))
                }
                useAddonAfter={true}
              />
            </PendingUpdatesHighlight>
          );
        })}
    </StyledTariffUnitQuantityWrapperDiv>
  );
};

export default memo(LineItemTariffUnitQuantities);
